import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  User,
  Phone,
  MapPin,
  Shield,
  Plus,
  Pencil,
  Trash2,
} from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DatePicker,
  VirtualizedFormSelect,
  Button,
} from '@roger/ui-components';
import {
  updatePatient,
  getAvailableClinicians,
  getCurrentEpisode,
  updateCurrentEpisodeCaseManager,
  updateEpisodeAssignments,
  listAgencyClinicPhysicians,
  listPatientInsurances,
  deletePatientInsurance,
} from '../../services/api';
import type {
  PatientResponse,
  PatientUpdate,
  Gender,
  AvailableClinicianResponse,
  ClinicPhysicianResponse,
  PatientInsuranceResponse,
} from '../../services/api/types.gen';
import { formatPhysicianName } from '../../utils/formatting';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { useToast } from '../../hooks/useToastContext';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessage,
} from '../../utils/apiErrorDetail';
import {
  parseISOToLocalDate,
  formatDateToISO,
  formatDateToMMDDYYYY,
} from '../../utils/dateUtils';
import { formatSSN } from '../../utils/intake';
import { GENDER_OPTIONS, US_STATES } from '../../constants/options';
import { AutoSavePill } from '../AutoSavePill';
import { PatientTestStatusToggle } from './PatientTestStatusToggle';

interface PatientInfoTabProps {
  patient: PatientResponse;
  onPatientUpdated: (patient: PatientResponse) => void;
  onAddInsurance?: () => void;
  onEditInsurance?: (insuranceId: string) => void;
  insuranceRefreshTrigger?: number;
  highlightSection?: 'insurance';
}

type FormState = {
  first_name: string;
  last_name: string;
  middle_name: string;
  date_of_birth: Date | undefined;
  gender: Gender | '';
  ssn: string;
  mbi: string;
  mrn: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
};

function patientToForm(patient: PatientResponse): FormState {
  return {
    first_name: patient.first_name ?? '',
    last_name: patient.last_name ?? '',
    middle_name: patient.middle_name ?? '',
    date_of_birth: patient.date_of_birth
      ? (parseISOToLocalDate(patient.date_of_birth) ?? undefined)
      : undefined,
    gender: (patient.gender as Gender) ?? '',
    ssn: patient.ssn ? formatSSN(patient.ssn) : '',
    mbi: patient.mbi ?? '',
    mrn: patient.mrn ?? '',
    phone: patient.phone ?? '',
    email: patient.email ?? '',
    address: patient.address ?? '',
    city: patient.city ?? '',
    state: patient.state ?? '',
    zip_code: patient.zip_code ?? '',
  };
}

const AUTOSAVE_DELAY_MS = 800;

export const PatientInfoTab: React.FC<PatientInfoTabProps> = ({
  patient,
  onPatientUpdated,
  onAddInsurance,
  onEditInsurance,
  insuranceRefreshTrigger,
  highlightSection,
}) => {
  const { error: showError } = useToast();
  const [form, setForm] = useState<FormState>(() => patientToForm(patient));
  const [saveStatus, setSaveStatus] = useState<
    'idle' | 'unsaved' | 'saving' | 'saved' | 'error'
  >('idle');
  // Bumps on each debounced edit so the pill restarts its countdown bar.
  const [changeToken, setChangeToken] = useState(0);
  const insuranceSectionRef = useRef<HTMLDivElement>(null);

  const [caseManagers, setCaseManagers] = useState<
    AvailableClinicianResponse[]
  >([]);
  const [currentCaseManagerId, setCurrentCaseManagerId] = useState<
    string | null
  >(null);
  const [episodeStartDate, setEpisodeStartDate] = useState<string | null>(null);
  const [episodeEndDate, setEpisodeEndDate] = useState<string | null>(null);
  const [physicianName, setPhysicianName] = useState<string | null>(null);
  const [currentPhysicianId, setCurrentPhysicianId] = useState<string | null>(
    null
  );
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [episodeId, setEpisodeId] = useState<string | null>(null);
  const [episodeLoaded, setEpisodeLoaded] = useState(false);

  const [insurances, setInsurances] = useState<PatientInsuranceResponse[]>([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showHighlight, setShowHighlight] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFormRef = useRef(form);
  latestFormRef.current = form;
  const isMountRef = useRef(true);
  // Synchronous mirror of changeToken: each savable edit bumps it, and a save
  // captures it at start so a stale completion can't report "saved" over a
  // newer queued edit.
  const editGenerationRef = useRef(0);

  useEffect(() => {
    setForm(patientToForm(patient));
    isMountRef.current = true;
  }, [patient]);

  const loadInsurances = useCallback(async () => {
    setInsurancesLoading(true);
    try {
      const res = await listPatientInsurances({
        path: { patient_id: patient.id },
      });
      if (res.data) setInsurances(res.data);
    } catch (err) {
      console.error('Failed to load patient insurances:', err);
      showError('Unable to load insurance information. Please try again.');
    } finally {
      setInsurancesLoading(false);
    }
  }, [patient.id, showError]);

  useEffect(() => {
    let cancelled = false;
    setCaseManagers([]);
    setCurrentCaseManagerId(null);
    setEpisodeStartDate(null);
    setEpisodeEndDate(null);
    setPhysicianName(null);
    setCurrentPhysicianId(null);
    setPhysicians([]);
    setEpisodeId(null);
    setEpisodeLoaded(false);

    const fetchEpisodeAndCaseManagers = async () => {
      try {
        const episodeResponse = await getCurrentEpisode({
          path: { patient_id: patient.id },
        });

        if (cancelled) return;

        if (episodeResponse?.data) {
          setEpisodeId(episodeResponse.data.episode_id);
          setCurrentCaseManagerId(episodeResponse.data.case_manager_id ?? null);
          setEpisodeStartDate(episodeResponse.data.start_date ?? null);
          setEpisodeEndDate(episodeResponse.data.end_date ?? null);
          setPhysicianName(episodeResponse.data.physician_name ?? null);
          setCurrentPhysicianId(episodeResponse.data.physician_id ?? null);

          const clinicId = episodeResponse.data.agency_clinic_id;
          if (clinicId) {
            const [cliniciansResponse, physiciansResponse] = await Promise.all([
              getAvailableClinicians({
                query: { clinic_id: clinicId },
              }),
              listAgencyClinicPhysicians({
                path: { agency_clinic_id: clinicId },
              }),
            ]);
            if (!cancelled && cliniciansResponse?.data) {
              setCaseManagers(
                cliniciansResponse.data.filter(c => c.is_case_manager)
              );
            }
            if (!cancelled && physiciansResponse?.data) {
              setPhysicians(physiciansResponse.data);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching episode data:', err);
          showError('Unable to load episode data. Please try again.');
        }
      } finally {
        if (!cancelled) setEpisodeLoaded(true);
      }
    };

    fetchEpisodeAndCaseManagers();
    loadInsurances();
    return () => {
      cancelled = true;
    };
  }, [patient.id, loadInsurances]);

  // Reload insurances when refresh trigger changes
  useEffect(() => {
    if (insuranceRefreshTrigger !== undefined && insuranceRefreshTrigger > 0) {
      void loadInsurances();
    }
  }, [insuranceRefreshTrigger, loadInsurances]);

  // Scroll to insurance section when highlighted
  useEffect(() => {
    if (highlightSection === 'insurance' && insuranceSectionRef.current) {
      // Show highlight immediately
      setShowHighlight(true);

      // Delay to ensure the tab content is fully rendered
      const scrollTimer = setTimeout(() => {
        insuranceSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);

      // Remove highlight after 1 second
      const hideTimer = setTimeout(() => {
        setShowHighlight(false);
      }, 1000);

      return () => {
        clearTimeout(scrollTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [highlightSection]);

  const isDirty = useCallback(
    (current: FormState) => {
      const original = patientToForm(patient);
      return (Object.keys(original) as (keyof FormState)[]).some(key => {
        const a = current[key];
        const b = original[key];
        if (a instanceof Date && b instanceof Date) {
          return a.getTime() !== b.getTime();
        }
        if (a instanceof Date || b instanceof Date) {
          return (a === undefined) !== (b === undefined);
        }
        return a !== b;
      });
    },
    [patient]
  );

  const doSave = useCallback(
    async (formSnapshot: FormState) => {
      if (!formSnapshot.first_name.trim() || !formSnapshot.last_name.trim()) {
        return;
      }
      if (!isDirty(formSnapshot)) return;

      // Tie this save to the latest edit so a stale completion can't report
      // "saved" over a newer queued edit.
      const savingGeneration = editGenerationRef.current;
      setSaveStatus('saving');
      try {
        const body: PatientUpdate = {
          first_name: formSnapshot.first_name.trim(),
          last_name: formSnapshot.last_name.trim(),
          middle_name: formSnapshot.middle_name.trim() || null,
          date_of_birth: formSnapshot.date_of_birth
            ? formatDateToISO(formSnapshot.date_of_birth)
            : null,
          gender: formSnapshot.gender || null,
          ssn: formSnapshot.ssn.replace(/\D/g, '') || null,
          mbi: formSnapshot.mbi.trim() || null,
          phone: formSnapshot.phone.trim() || null,
          email: formSnapshot.email.trim() || null,
          address: formSnapshot.address.trim() || null,
          city: formSnapshot.city.trim() || null,
          state: formSnapshot.state || null,
          zip_code: formSnapshot.zip_code.trim() || null,
        };

        const response = await updatePatient({
          path: { patient_id: patient.id },
          body,
        });

        if (response?.data) {
          onPatientUpdated(response.data);
          // Suppress the saved indicator if a newer edit arrived while this
          // save was in flight; the pending edit's own save will report it.
          if (editGenerationRef.current === savingGeneration) {
            setSaveStatus('saved');
            // Only fall back to idle if a newer edit hasn't already moved on.
            setTimeout(
              () => setSaveStatus(s => (s === 'saved' ? 'idle' : s)),
              2000
            );
          }
        } else {
          setSaveStatus('error');
          showError(
            getUserFacingApiMessage(
              response,
              'Failed to save patient information'
            )
          );
          setTimeout(
            () => setSaveStatus(s => (s === 'error' ? 'idle' : s)),
            3000
          );
        }
      } catch {
        setSaveStatus('error');
        showError('Failed to save patient information');
        setTimeout(
          () => setSaveStatus(s => (s === 'error' ? 'idle' : s)),
          3000
        );
      }
    },
    [patient.id, isDirty, onPatientUpdated, showError]
  );

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }

    // Surface the pending/countdown state on the pill while the debounce
    // timer runs, but only when the edit will actually save (dirty + the
    // required name fields present); otherwise clear a stale pending state.
    const willSave =
      Boolean(form.first_name.trim() && form.last_name.trim()) && isDirty(form);
    if (willSave) {
      setSaveStatus('unsaved');
      setChangeToken(t => t + 1);
      editGenerationRef.current += 1;
    } else {
      setSaveStatus(s => (s === 'unsaved' ? 'idle' : s));
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      doSave(latestFormRef.current);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [form, doSave, isDirty]);

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleCaseManagerChange = async (value: string) => {
    const previousId = currentCaseManagerId;
    setCurrentCaseManagerId(value);
    try {
      const response = await updateCurrentEpisodeCaseManager({
        path: { patient_id: patient.id },
        body: { case_manager_id: value },
      });
      if (!response?.data) {
        setCurrentCaseManagerId(previousId);
        showError(
          getUserFacingApiMessage(response, 'Failed to update case manager')
        );
      }
    } catch {
      setCurrentCaseManagerId(previousId);
      showError('Failed to update case manager');
    }
  };

  const handlePhysicianChange = async (value: string) => {
    if (!episodeId) return;
    const previousId = currentPhysicianId;
    setCurrentPhysicianId(value);
    try {
      const response = await updateEpisodeAssignments({
        path: { episode_id: episodeId },
        body: { clinic_physician_id: value },
      });
      if (!response?.data) {
        setCurrentPhysicianId(previousId);
        showError('Failed to update physician');
      }
    } catch {
      setCurrentPhysicianId(previousId);
      showError('Failed to update physician');
    }
  };

  const handleDeleteInsurance = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      // The API client is configured throwOnError:false, so an HTTP failure
      // resolves to `{ error }` rather than throwing — inspect it explicitly.
      const resp = await deletePatientInsurance({
        path: { patient_id: patient.id, patient_insurance_id: id },
      });
      if (resp.error) {
        console.error('Failed to delete insurance:', resp.error);
        showError(
          getBusinessErrorMessage(resp) ??
            'Unable to delete insurance. Please try again.'
        );
        return;
      }
      await loadInsurances();
    } catch (err: unknown) {
      console.error('Failed to delete insurance:', err);
      showError('Unable to delete insurance. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  // Build physician dropdown options from the clinic roster. If the episode's
  // current physician isn't in the active roster (e.g. soft-deleted), prepend a
  // synthetic option so the assignment still renders rather than going blank.
  const physicianOptions = physicians.map(p => ({
    value: p.id,
    label: formatPhysicianName(p),
  }));
  if (
    currentPhysicianId &&
    !physicianOptions.some(o => o.value === currentPhysicianId)
  ) {
    physicianOptions.unshift({
      value: currentPhysicianId,
      label: physicianName ?? 'Current physician',
    });
  }

  return (
    <div className='space-y-6 max-w-4xl'>
      {/* Auto-save status pill: a zero-height sticky overlay that floats over
          the top-right of the form and stays in view while it scrolls. Zero
          height means showing/hiding the pill never shifts the layout. */}
      <div className='pointer-events-none sticky top-0 z-20 flex h-0 justify-end'>
        <AutoSavePill
          status={saveStatus}
          debounceMs={AUTOSAVE_DELAY_MS}
          changeToken={changeToken}
        />
      </div>

      {/* Demographic Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <User className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Demographic Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='first_name'>
                First Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='first_name'
                value={form.first_name}
                onChange={e => updateField('first_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='middle_name'>Middle Name</Label>
              <Input
                id='middle_name'
                value={form.middle_name}
                onChange={e => updateField('middle_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='last_name'>
                Last Name <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='last_name'
                value={form.last_name}
                onChange={e => updateField('last_name', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Date of Birth</Label>
              <DatePicker
                date={form.date_of_birth}
                onDateChange={d => updateField('date_of_birth', d)}
                datePickerSize='sm'
                className='w-full'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Gender</Label>
              <Select
                value={form.gender}
                onValueChange={v => updateField('gender', v as Gender)}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue placeholder='Select gender' />
                </SelectTrigger>
                <SelectContent>
                  {GENDER_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='ssn'>SSN</Label>
              <Input
                id='ssn'
                value={form.ssn}
                onChange={e => updateField('ssn', formatSSN(e.target.value))}
                placeholder='XXX-XX-XXXX'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mbi'>MBI</Label>
              <Input
                id='mbi'
                value={form.mbi}
                onChange={e => updateField('mbi', e.target.value)}
                placeholder='1EG4-TE5-MK72'
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='mrn'>MRN</Label>
              <Input
                id='mrn'
                value={form.mrn}
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <Phone className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Contact Information</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='phone'>Phone</Label>
              <Input
                id='phone'
                value={form.phone}
                onChange={e => updateField('phone', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='email'>Email</Label>
              <Input
                id='email'
                type='email'
                value={form.email}
                onChange={e => updateField('email', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='md:col-span-3 space-y-1.5'>
              <Label htmlFor='address'>Street Address</Label>
              <Input
                id='address'
                value={form.address}
                onChange={e => updateField('address', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='city'>City</Label>
              <Input
                id='city'
                value={form.city}
                onChange={e => updateField('city', e.target.value)}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>State</Label>
              <Select
                value={form.state}
                onValueChange={v => updateField('state', v)}
              >
                <SelectTrigger selectSize='sm'>
                  <SelectValue placeholder='Select state' />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map(st => (
                    <SelectItem key={st.value} value={st.value}>
                      {st.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='zip_code'>ZIP Code</Label>
              <Input
                id='zip_code'
                value={form.zip_code}
                onChange={e => updateField('zip_code', e.target.value)}
                inputSize='sm'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Episode */}
      <Card>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-2'>
            <MapPin className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Current Episode</h3>
          </div>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <div className='space-y-1.5'>
              <Label>Case Manager</Label>
              <ClinicianSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={
                  !episodeLoaded ? 'Loading...' : 'Select case manager'
                }
                clinicians={caseManagers}
                defaultValue={currentCaseManagerId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handleCaseManagerChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Physician</Label>
              <VirtualizedFormSelect
                key={patient.id}
                label=''
                selectSize='sm'
                placeholder={!episodeLoaded ? 'Loading...' : 'Select physician'}
                options={physicianOptions}
                sortOptions
                defaultValue={currentPhysicianId ?? ''}
                disabled={!episodeLoaded}
                onChange={e => handlePhysicianChange(e.target.value)}
                isClearable={false}
                menuPosition='absolute'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode Start Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeStartDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Episode End Date</Label>
              <Input
                value={
                  episodeLoaded
                    ? formatDateToMMDDYYYY(episodeEndDate) || '—'
                    : 'Loading...'
                }
                disabled
                inputSize='sm'
                className='bg-muted'
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Insurance Information */}
      <Card
        ref={insuranceSectionRef}
        className={
          showHighlight
            ? 'ring-2 ring-primary ring-offset-2 transition-all duration-500'
            : 'transition-all duration-500'
        }
      >
        <CardHeader className='pb-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Shield className='h-4 w-4 text-muted-foreground' />
              <h3 className='text-sm font-semibold'>Insurance Information</h3>
            </div>
            {onAddInsurance && (
              <Button size='sm' onClick={onAddInsurance}>
                <Plus className='h-3.5 w-3.5 mr-1.5' />
                Add Insurance
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {insurancesLoading ? (
            <p className='text-sm text-muted-foreground py-6 text-center'>
              Loading…
            </p>
          ) : insurances.length === 0 ? (
            <p className='text-sm text-muted-foreground py-6 text-center'>
              No insurance policies on file.
            </p>
          ) : (
            <div className='divide-y'>
              {insurances.map(ins => (
                <div
                  key={ins.id}
                  className='py-3 flex items-center justify-between gap-3 group'
                >
                  <div className='flex items-center gap-3 min-w-0'>
                    <div className='min-w-0'>
                      <p className='text-sm font-medium truncate'>
                        {ins.insurance_name}
                      </p>
                      <p className='text-xs text-muted-foreground'>
                        {[
                          ins.plan_name,
                          ins.policy_number
                            ? `Policy: ${ins.policy_number}`
                            : null,
                          ins.effective_start
                            ? `${formatDateToMMDDYYYY(ins.effective_start)}${ins.effective_end ? ` – ${formatDateToMMDDYYYY(ins.effective_end)}` : ''}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-center gap-1'>
                    {onEditInsurance && (
                      <button
                        type='button'
                        onClick={() => onEditInsurance(ins.id)}
                        className='shrink-0 p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors'
                        aria-label='Edit insurance'
                      >
                        <Pencil className='h-3.5 w-3.5' />
                      </button>
                    )}
                    <button
                      type='button'
                      disabled={insurances.length <= 1 || deletingId === ins.id}
                      onClick={e => void handleDeleteInsurance(ins.id, e)}
                      className='shrink-0 p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                      aria-label='Delete insurance'
                    >
                      <Trash2 className='h-3.5 w-3.5' />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test / Practice Patient — last section; explains itself before the toggle */}
      <PatientTestStatusToggle
        patientId={patient.id}
        isTest={!!patient.is_test_patient}
        onUpdated={onPatientUpdated}
      />
    </div>
  );
};
